import React, { useEffect, useState, useCallback } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { MoreHorizontal, Search, Star, Trash2, Eye, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl, PLACEHOLDER_IMAGE } from '@/api/client';
import AdminTable from '@/components/AdminTable';

const STATUS_BADGE = {
  active:         { style: { backgroundColor: '#dcfce7', color: '#16a34a' } }, // Success
  draft:          { style: { backgroundColor: '#f1f5f9', color: '#64748b' } }, // Secondary
  sold:           { style: { backgroundColor: '#fee2e2', color: '#dc2626' } }, // Danger
  pending_review: { style: { backgroundColor: '#fef3c7', color: '#d97706' } }, // Warning
  rejected:       { style: { backgroundColor: '#f3f4f6', color: '#374151' } },
};

const STATUS_LABEL = {
  active: 'Active', draft: 'Draft', sold: 'Sold',
  pending_review: 'Pending Review', rejected: 'Rejected',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatPrice = (p) => {
  if (!p && p !== 0) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(p);
};

export default function AllListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Reject dialog
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Refund dialog
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);

  const fetchListings = useCallback((status) => {
    setLoading(true);
    const params = status === 'all' ? '' : `?status=${status}`;
    api.get(`/admin/listings${params}`)
      .then(r => setListings(r.data))
      .catch(() => toast.error('Failed to load listings'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchListings(statusFilter); }, [statusFilter, fetchListings]);

  const filtered = listings.filter(l =>
    !search ||
    l.title?.toLowerCase().includes(search.toLowerCase()) ||
    l.seller_name?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleFeature = async (id, featured) => {
    try {
      await api.put(`/admin/listings/${id}/feature`);
      setListings(prev => prev.map(l => l.id === id ? { ...l, featured: !featured } : l));
      toast.success(featured ? 'Removed from featured' : 'Listing featured');
    } catch {
      toast.error('Failed to update feature status');
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/admin/listings/${id}/approve`);
      toast.success('Listing approved and live');
      fetchListings(statusFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve listing');
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await api.put(`/admin/listings/${rejectTarget.id}/reject`, { admin_notes: rejectNote });
      toast.success('Listing rejected');
      setRejectTarget(null);
      setRejectNote('');
      fetchListings(statusFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject listing');
    } finally {
      setRejecting(false);
    }
  };

  const handleRefundConfirm = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      const r = await api.post(`/admin/listings/${refundTarget.id}/refund`);
      toast.success(`Refund issued — £${r.data.amount?.toFixed(2) || ''}`);
      setRefundTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/listings/${deleteId}`);
      toast.success('Listing deleted');
      setDeleteId(null);
      fetchListings(statusFilter);
    } catch {
      toast.error('Failed to delete listing');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardShell title="All Listings">
      <div className="space-y-5">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 bg-white border-gray-200 rounded-xl">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-100 shadow-lg rounded-xl">
              <SelectItem value="all" className="hover:bg-gray-50 cursor-pointer">All statuses</SelectItem>
              <SelectItem value="pending_review" className="hover:bg-gray-50 cursor-pointer">Pending Review</SelectItem>
              <SelectItem value="active" className="hover:bg-gray-50 cursor-pointer">Active</SelectItem>
              <SelectItem value="rejected" className="hover:bg-gray-50 cursor-pointer">Rejected</SelectItem>
              <SelectItem value="draft" className="hover:bg-gray-50 cursor-pointer">Draft</SelectItem>
              <SelectItem value="sold" className="hover:bg-gray-50 cursor-pointer">Sold</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative w-full sm:w-60">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search title or seller…"
              className="pl-8 text-sm bg-white border-gray-200 rounded-xl focus:ring-blue-500 focus:border-blue-500 shadow-sm"
            />
          </div>
        </div>

        {/* Table */}
        <AdminTable
          data={filtered}
          loading={loading}
          dataTestId="admin-listings-table"
          emptyTitle="No listings found"
          emptySubtitle="Try adjusting your filters or search."
          columns={[
            {
              key: 'listing', label: 'Listing',
              render: (l) => {
                const img = l.images?.[0] ? getImageUrl(l.images[0]) : PLACEHOLDER_IMAGE;
                return (
                  <div className="flex items-center gap-3">
                    <img src={img} alt={l.title} className="w-10 h-10 rounded-lg object-cover flex-shrink-0 bg-gray-100" />
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{l.title}</p>
                      <p className="text-xs text-gray-500">{l.category_name}</p>
                    </div>
                    {l.featured && <Star size={13} fill="currentColor" className="text-amber-500 flex-shrink-0" />}
                  </div>
                );
              },
            },
            { key: 'seller', label: 'Seller', render: (l) => <span className="text-gray-900 font-medium">{l.seller_name}</span> },
            { key: 'price', label: 'Price', render: (l) => <span className="text-gray-900 font-medium">{formatPrice(l.price)}</span> },
            { key: 'status', label: 'Status', render: (l) => {
              const badge = STATUS_BADGE[l.status] || STATUS_BADGE.draft;
              const label = STATUS_LABEL[l.status] || l.status;
              return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold" style={badge.style}>{label}</span>;
            } },
            {
              key: 'actions', label: '', width: '60px',
              render: (l) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-gray-100 rounded-xl" data-testid={`listing-actions-${l.id}`}>
                      <MoreHorizontal size={15} className="text-gray-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white border border-gray-100 shadow-lg rounded-xl">
                    <DropdownMenuItem asChild className="gap-2 cursor-pointer hover:bg-gray-50 rounded-lg">
                      <a href={`/listings/${l.id}`} target="_blank" rel="noreferrer"><Eye size={14} className="text-gray-500" /> <span className="text-gray-700 font-medium">View listing</span></a>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => toggleFeature(l.id, l.featured)} className="gap-2 cursor-pointer hover:bg-gray-50 rounded-lg">
                      <Star size={14} className="text-gray-500" /> <span className="text-gray-700 font-medium">{l.featured ? 'Remove featured' : 'Feature listing'}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {l.status === 'pending_review' && (
                      <>
                        <DropdownMenuItem
                          onClick={() => handleApprove(l.id)}
                          className="gap-2 cursor-pointer hover:bg-green-50 rounded-lg text-green-600 font-medium"
                          data-testid={`approve-listing-${l.id}`}
                        >
                          <CheckCircle size={14} /> Approve listing
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => { setRejectTarget(l); setRejectNote(''); }}
                          className="gap-2 cursor-pointer hover:bg-red-50 rounded-lg text-red-600 font-medium"
                          data-testid={`reject-listing-${l.id}`}
                        >
                          <XCircle size={14} /> Reject listing
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    {l.status === 'active' && (
                      <>
                        <DropdownMenuItem
                          onClick={() => { setRejectTarget(l); setRejectNote(''); }}
                          className="gap-2 cursor-pointer hover:bg-red-50 rounded-lg text-red-600 font-medium"
                        >
                          <XCircle size={14} /> Reject & unpublish
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem
                      onClick={() => setRefundTarget(l)}
                      className="gap-2 cursor-pointer hover:bg-purple-50 rounded-lg text-purple-600 font-medium"
                      data-testid={`refund-listing-${l.id}`}
                    >
                      <RefreshCw size={14} /> Issue refund
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setDeleteId(l.id)}
                      className="gap-2 cursor-pointer hover:bg-red-50 rounded-lg text-red-600 font-medium"
                    >
                      <Trash2 size={14} /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            },
          ]}
        />
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white border-0 shadow-xl rounded-2xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-gray-900">Delete listing?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This action cannot be undone. The listing will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-xl font-semibold text-gray-600 hover:bg-gray-100 border-gray-200">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="rounded-xl font-semibold text-white bg-red-600 hover:bg-red-700 shadow-sm"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={() => setRejectTarget(null)}>
        <DialogContent className="bg-white border-0 shadow-xl rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              Reject listing
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">
            Rejecting <strong className="text-gray-900">{rejectTarget?.title}</strong>. The member will be notified by email.
          </p>
          <div className="py-2">
            <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Note to member (optional)</label>
            <Textarea
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
              placeholder="Reason for rejection…"
              rows={3}
              className="text-sm border-gray-200 rounded-xl focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              data-testid="reject-note-input"
            />
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="ghost" onClick={() => setRejectTarget(null)} className="rounded-xl font-semibold text-gray-600 hover:bg-gray-100 border-gray-200">Cancel</Button>
            <Button
              onClick={handleRejectConfirm}
              disabled={rejecting}
              className="rounded-xl font-semibold text-white bg-red-600 hover:bg-red-700 shadow-sm"
              data-testid="confirm-reject-btn"
            >
              {rejecting ? 'Rejecting…' : 'Reject Listing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <AlertDialog open={!!refundTarget} onOpenChange={() => setRefundTarget(null)}>
        <AlertDialogContent className="bg-white border-0 shadow-xl rounded-2xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-gray-900">Issue refund?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will issue a full refund for the listing fee paid for <strong className="text-gray-900">{refundTarget?.title}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="rounded-xl font-semibold text-gray-600 hover:bg-gray-100 border-gray-200">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRefundConfirm}
              disabled={refunding}
              className="rounded-xl font-semibold text-white bg-purple-600 hover:bg-purple-700 shadow-sm"
              data-testid="confirm-refund-btn"
            >
              {refunding ? 'Processing…' : 'Issue Refund'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}
